import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Chip,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
} from "@mui/material";
import { RemoveCircleOutline } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useQueryClient } from "@tanstack/react-query";

const LabTestDialog = ({
  open,
  onClose,
  patientId,
  doctorId,
  ticketId = null,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const queryClient = useQueryClient();

  const { data: testsData } = useFetchObjects(
    ["laboratory-tests"],
    "laboratory-tests/",
    token,
  );

  const { handleAdd, loading, resetForm } = useAdd(
    "laboratory-test-orders",
    token,
    t("profile.labTestAdded") || "Lab test order created successfully",
    t("profile.labTestAddFailed") || "Failed to create lab test order",
  );

  const [selectedTests, setSelectedTests] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [formData, setFormData] = useState({
    urgency: false,
    discount: 0,
  });
  const [errors, setErrors] = useState({});

  const tests = testsData?.results || testsData || [];
  const parentTests = tests.filter(
    (test) =>
      !test.parent_test &&
      (test.is_parent_test === true || test.is_parent_test === undefined),
  );

  useEffect(() => {
    if (resetForm) {
      setSelectedTests([]);
      setSearchText("");
      setFormData({ urgency: false, discount: 0 });
      setErrors({});
      onClose();
      queryClient.invalidateQueries(["unified-ehr", patientId]);
    }
  }, [resetForm, onClose, queryClient, patientId]);

  const handleAddTest = (test) => {
    if (test && !selectedTests.find((t) => t.id === test.id)) {
      setSelectedTests([...selectedTests, test]);
      setSearchText("");
    }
  };

  const handleRemoveTest = (index) => {
    setSelectedTests(selectedTests.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (selectedTests.length === 0) {
      setErrors({
        tests:
          t("profile.selectAtLeastOneTest") ||
          "Please select at least one test",
      });
      return;
    }

    const submitData = {
      patient: patientId,
      doctor: doctorId,
      ticket: ticketId || null,
      tests: selectedTests.map((test) => ({ test_type: test.id })),
      urgency: formData.urgency,
      discount: parseFloat(formData.discount) || 0,
    };

    handleAdd(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("profile.addLabTest") || "Add Lab Test Order"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={parentTests}
              getOptionLabel={(option) => option.name || ""}
              inputValue={searchText}
              onInputChange={(e, newValue) => setSearchText(newValue)}
              onChange={(e, newValue) => handleAddTest(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("profile.searchTests") || "Search Tests"}
                  placeholder={t("profile.typeToSearch") || "Type to search..."}
                />
              )}
            />
          </Grid>

          {selectedTests.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t("profile.selectedTests") || "Selected Tests"}:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectedTests.map((test, index) => (
                  <Chip
                    key={test.id}
                    label={test.name}
                    onDelete={() => handleRemoveTest(index)}
                    deleteIcon={<RemoveCircleOutline />}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {errors.tests && (
            <Grid item xs={12}>
              <Alert severity="error">{errors.tests}</Alert>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.urgency}
                  onChange={(e) =>
                    setFormData({ ...formData, urgency: e.target.checked })
                  }
                />
              }
              label={t("profile.urgent") || "Urgent"}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("profile.discount") || "Discount"}
              type="number"
              value={formData.discount}
              onChange={(e) =>
                setFormData({ ...formData, discount: e.target.value })
              }
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("profile.cancel") || "Cancel"}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading
            ? t("profile.saving") || "Saving..."
            : t("profile.add") || "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LabTestDialog;
